/**
 * NoCompanyPage — C-2-1 + C-2-2. 소속없음 빈화면 (직원 전용)
 *
 * C-2-1: 진입 시 join-status 조회 → PENDING이면 B-2(/app/welcome)로 이동
 * C-2-2: 소속 해제 상태 빈화면 → [새 소속 신청하기] → /app/biz-check
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@indipia/ui-core';
import { useQuery } from '@tanstack/react-query';
import { fetchJoinStatus } from '../api/employees';

export function NoCompanyPage() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['employees', 'join-status'],
    queryFn: fetchJoinStatus,
    retry: 1,
  });

  // C-2-1: PENDING 상태면 B-2 직원 웰컴 화면으로 이동
  useEffect(() => {
    if (data?.staffStatus === 'PENDING') {
      navigate('/app/welcome', { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-point" />
      </div>
    );
  }

  // C-2-2. 소속 없음 화면
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex max-w-sm flex-col items-center gap-6 px-6 text-center">

        {/* 일러스트 */}
        <div className="relative inline-block">
          <svg width="140" height="140" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="54" fill="var(--warm-peach-500)" fillOpacity=".07"/>
            <ellipse cx="60" cy="103" rx="30" ry="4" fill="var(--warm-peach-500)" fillOpacity=".1"/>
            <rect x="20" y="38" width="80" height="62" rx="4" fill="var(--shell-surface)" stroke="var(--shell-border)" strokeWidth="2" strokeDasharray="5 3"/>
            <polygon points="60,14 104,42 16,42" fill="var(--warm-peach-500)" fillOpacity=".12" stroke="var(--shell-border)" strokeWidth="1.8" strokeDasharray="5 3" strokeLinejoin="round"/>
            <rect x="28" y="52" width="16" height="14" rx="2" fill="var(--warm-peach-500)" fillOpacity=".12" stroke="var(--shell-border)" strokeWidth="1.2" strokeDasharray="4 2"/>
            <rect x="52" y="52" width="16" height="14" rx="2" fill="var(--warm-peach-500)" fillOpacity=".12" stroke="var(--shell-border)" strokeWidth="1.2" strokeDasharray="4 2"/>
            <rect x="76" y="52" width="16" height="14" rx="2" fill="var(--warm-peach-500)" fillOpacity=".12" stroke="var(--shell-border)" strokeWidth="1.2" strokeDasharray="4 2"/>
            <rect x="45" y="72" width="30" height="28" rx="3" fill="var(--warm-peach-500)" fillOpacity=".12" stroke="var(--shell-border)" strokeWidth="1.5" strokeDasharray="4 2"/>
          </svg>
          <div className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-surface-raised shadow-md">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-group">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>

        <div>
          <p className="text-xl font-bold text-text">현재 소속된 업체가 없어요</p>
          <p className="mt-2 text-sm leading-relaxed text-text-muted">
            마스터 계정의 소속 해제로<br />서비스 이용이 제한됐어요
          </p>
        </div>

        {/* 이전 소속 정보 — join-status에 companyName 있을 때만 표시 */}
        {data?.companyName && (
          <div className="w-full rounded-xl border border-border bg-surface-raised px-4 py-3 text-left">
            <p className="mb-2 text-xs text-text-muted">이전 소속 정보</p>
            <div className="flex gap-3 text-sm">
              <span className="w-16 shrink-0 text-text-muted">이전 소속</span>
              <span className="font-medium text-text">{data.companyName}</span>
            </div>
          </div>
        )}

        <Button type="button" variant="primary" onClick={() => navigate('/app/biz-check')} className="w-full">
          새 소속 신청하기
        </Button>
      </div>
    </div>
  );
}
